import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface ClinicianProfile {
  id: string;
  user_id: string;
  practice_name: string | null;
  professional_title: string | null;
  qualifications: string[];
  registration_body: string | null;
  registration_number: string | null;
  specializations: string[];
  bio: string | null;
  practice_address: string | null;
  practice_phone: string | null;
  practice_email: string | null;
  website_url: string | null;
  working_days: string[];
  working_hours_start: string;
  working_hours_end: string;
  session_duration_default: number;
  buffer_between_sessions: number;
  standard_session_fee: number | null;
  currency: string;
  cancellation_policy: string | null;
  cancellation_notice_hours: number;
  cancellation_fee_percentage: number;
  logo_url: string | null;
  accent_color: string | null;
}

export interface ClinicianClient {
  id: string;
  clinician_id: string;
  client_id: string;
  status: string;
  invited_at: string;
  accepted_at: string | null;
  archived_at: string | null;
  notes: string | null;
  therappi_connected: boolean;
  last_activity_at: string | null;
  client_profile?: {
    display_name: string | null;
    avatar_url: string | null;
  };
}

export interface TherapySession {
  id: string;
  clinician_id: string;
  client_id: string | null;
  client_name: string | null;
  session_date: string;
  session_time: string;
  duration_minutes: number;
  session_type: string;
  location_type: string;
  location_details: string | null;
  status: string;
  session_fee: number | null;
  currency: string;
  payment_status: string;
  payment_date: string | null;
  payment_method: string | null;
  invoice_number: string | null;
  pre_session_notes: string | null;
  session_notes: string | null;
  post_session_notes: string | null;
  homework_assigned: string | null;
  cancellation_reason: string | null;
  cancellation_fee_applied: boolean;
  cancelled_at: string | null;
  cancelled_by: string | null;
  reminder_sent: boolean;
  created_at: string;
  updated_at: string;
}

export interface ClinicianSubscription {
  id: string;
  clinician_id: string;
  tier_id: string;
  status: string;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  tier?: {
    name: string;
    max_clients: number | null;
    features: string[];
    price_gbp: number | null;
    is_enterprise: boolean;
  };
}

export function useClinicianPortal() {
  const [profile, setProfile] = useState<ClinicianProfile | null>(null);
  const [clients, setClients] = useState<ClinicianClient[]>([]);
  const [sessions, setSessions] = useState<TherapySession[]>([]);
  const [subscription, setSubscription] = useState<ClinicianSubscription | null>(null);
  const [clientCount, setClientCount] = useState(0);
  const [maxClients, setMaxClients] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [isClinicianMode, setIsClinicianMode] = useState(false);
  const { toast } = useToast();

  const fetchProfile = useCallback(async (userId: string) => {
    const { data, error } = await supabase
      .from('clinician_profiles')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (error && error.code !== 'PGRST116') {
      console.error('Error fetching clinician profile:', error);
      return null;
    }
    return data;
  }, []);

  const fetchClients = useCallback(async (userId: string) => {
    const { data, error } = await supabase
      .from('clinician_clients')
      .select('*')
      .eq('clinician_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching clients:', error);
      return [];
    }

    // Fetch client profiles separately
    const clientIds = data?.map(c => c.client_id) || [];
    if (clientIds.length > 0) {
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, display_name, avatar_url')
        .in('user_id', clientIds);

      const profileMap = new Map(profiles?.map(p => [p.user_id, p]) || []);
      return data?.map(client => ({
        ...client,
        client_profile: profileMap.get(client.client_id)
      })) || [];
    }

    return data || [];
  }, []);

  const fetchSessions = useCallback(async (userId: string) => {
    const { data, error } = await supabase
      .from('therapy_sessions')
      .select('*')
      .eq('clinician_id', userId)
      .order('session_date', { ascending: true })
      .order('session_time', { ascending: true });

    if (error) {
      console.error('Error fetching sessions:', error);
      return [];
    }
    return data || [];
  }, []);

  const fetchSubscription = useCallback(async (userId: string) => {
    const { data, error } = await supabase
      .from('clinician_subscriptions')
      .select(`
        *,
        tier:clinician_subscription_tiers(
          name,
          max_clients,
          features,
          price_gbp,
          is_enterprise
        )
      `)
      .eq('clinician_id', userId)
      .eq('status', 'active')
      .maybeSingle();

    if (error && error.code !== 'PGRST116') {
      console.error('Error fetching subscription:', error);
      return null;
    }
    return data;
  }, []);

  const loadAllData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const [profileData, clientsData, sessionsData, subData] = await Promise.all([
        fetchProfile(user.id),
        fetchClients(user.id),
        fetchSessions(user.id),
        fetchSubscription(user.id)
      ]);

      setProfile(profileData);
      setClients(clientsData);
      setSessions(sessionsData);
      setSubscription(subData);
      setClientCount(clientsData.filter(c => c.status === 'active').length);
      setMaxClients(subData?.tier?.max_clients || null);
      setIsClinicianMode(!!profileData || !!subData);
    } catch (error) {
      console.error('Error loading clinician data:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchProfile, fetchClients, fetchSessions, fetchSubscription]);

  const createProfile = async (profileData: Partial<ClinicianProfile>) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('clinician_profiles')
      .insert({ ...profileData, user_id: user.id })
      .select()
      .single();

    if (error) throw error;
    setProfile(data);
    setIsClinicianMode(true);
    return data;
  };

  const updateProfile = async (updates: Partial<ClinicianProfile>) => {
    if (!profile) throw new Error('No profile to update');

    const { data, error } = await supabase
      .from('clinician_profiles')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', profile.id)
      .select()
      .single();

    if (error) throw error;
    setProfile(data);
    return data;
  };

  const createSession = async (sessionData: Partial<TherapySession>) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const insertData = {
      clinician_id: user.id,
      client_id: sessionData.client_id || null,
      client_name: sessionData.client_name || null,
      session_date: sessionData.session_date!,
      session_time: sessionData.session_time!,
      duration_minutes: sessionData.duration_minutes || 60,
      session_type: sessionData.session_type || 'individual',
      location_type: sessionData.location_type || 'in_person',
      location_details: sessionData.location_details || null,
      status: sessionData.status || 'scheduled',
      session_fee: sessionData.session_fee || null,
      currency: sessionData.currency || 'GBP',
      payment_status: sessionData.payment_status || 'pending',
      pre_session_notes: sessionData.pre_session_notes || null,
      session_notes: sessionData.session_notes || null,
      homework_assigned: sessionData.homework_assigned || null
    };

    const { data, error } = await supabase
      .from('therapy_sessions')
      .insert(insertData)
      .select()
      .single();

    if (error) throw error;
    setSessions(prev => [...prev, data as TherapySession]);
    return data;
  };

  const updateSession = async (sessionId: string, updates: Partial<TherapySession>) => {
    const { data, error } = await supabase
      .from('therapy_sessions')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', sessionId)
      .select()
      .single();

    if (error) throw error;
    setSessions(prev => prev.map(s => s.id === sessionId ? data : s));
    return data;
  };

  const deleteSession = async (sessionId: string) => {
    const { error } = await supabase
      .from('therapy_sessions')
      .delete()
      .eq('id', sessionId);

    if (error) throw error;
    setSessions(prev => prev.filter(s => s.id !== sessionId));
  };

  const sendInvitation = async (email: string, message?: string) => {
    const { data, error } = await supabase.functions.invoke('send-clinician-invitation', {
      body: {
        recipient_email: email,
        invitation_type: 'clinician_to_client',
        message
      }
    });

    if (error) throw error;
    if (data.error) throw new Error(data.error);
    
    toast({
      title: "Invitation Sent",
      description: `Invitation sent to ${email}`
    });

    return data;
  };

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  return {
    profile,
    clients,
    sessions,
    subscription,
    clientCount,
    maxClients,
    loading,
    isClinicianMode,
    loadAllData,
    createProfile,
    updateProfile,
    createSession,
    updateSession,
    deleteSession,
    sendInvitation,
    canAddClient: maxClients === null || clientCount < maxClients
  };
}
